import { Card, CardContent } from '../../../components/ui';
import { MICROSITE_STATUS } from '../../../constants/api.constants';

interface SaveDialogProps {
    isOpen: boolean;
    onClose: () => void;
    status: 'loading' | 'success' | 'error';
    successType?: 'publish' | 'inactive' | 'live';
    errorMessage?: string;
    micrositeData?: {
        storeBrandName?: string;
        storeName?: string;
        url?: string;
        type?: string;
        status?: string;
    };
}

export default function SaveDialog({
    isOpen,
    onClose,
    status,
    successType = 'publish',
    errorMessage,
    micrositeData,
}: SaveDialogProps) {
    if (!isOpen) return null;

    // Determine the success message and description based on successType
    const getSuccessContent = () => {
        switch (successType) {
            case 'inactive':
                return {
                    title: 'Microsite Marked as Inactive',
                    description:
                        'The microsite has been successfully deactivated and is no longer accessible',
                    iconBg: 'bg-[#fff7ed]',
                    iconColor: 'text-[#f59e0b]',
                };
            case 'live':
                return {
                    title: 'Microsite is Now Live!',
                    description:
                        'The microsite has been activated and is now accessible to visitors',
                    iconBg: 'bg-[#dcfce7]',
                    iconColor: 'text-[#10b981]',
                };
            case 'publish':
            default:
                return {
                    title: 'Microsite Published Successfully!',
                    description: 'Your microsite is now live and accessible',
                    iconBg: 'bg-[#dcfce7]',
                    iconColor: 'text-[#10b981]',
                };
        }
    };

    const successContent = getSuccessContent();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <Card className="w-full max-w-md">
                <CardContent className="p-6">
                    {status === 'loading' && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="w-16 h-16 border-4 border-[#e2e8f0] border-t-[#6e64f0] rounded-full animate-spin mb-4"></div>
                            <h3 className="text-lg font-semibold text-[#1e293b] mb-2">
                                Saving Microsite...
                            </h3>
                            <p className="text-sm text-[#64748b] text-center">
                                Please wait while we save your microsite configuration
                            </p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-16 h-16 rounded-full ${successContent.iconBg} flex items-center justify-center mb-4`}
                                >
                                    <svg
                                        className={`w-8 h-8 ${successContent.iconColor}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#1e293b] mb-2">
                                    {successContent.title}
                                </h3>
                                <p className="text-sm text-[#64748b] text-center">
                                    {successContent.description}
                                </p>
                            </div>

                            {micrositeData && (
                                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4 space-y-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <p className="text-xs text-[#64748b] mb-1">Type</p>
                                            <p className="text-sm font-semibold text-[#1e293b] capitalize">
                                                {micrositeData.type || 'Brand'} Level
                                            </p>
                                        </div>
                                        <div
                                            className={`px-2 py-1 text-white text-xs font-medium rounded ${
                                                micrositeData.status === MICROSITE_STATUS.PUBLISHED
                                                    ? 'bg-[#10b981]'
                                                    : micrositeData.status ===
                                                        MICROSITE_STATUS.INACTIVE
                                                      ? 'bg-[#64748b]'
                                                      : 'bg-[#f59e0b]'
                                            }`}
                                        >
                                            {micrositeData.status === MICROSITE_STATUS.PUBLISHED
                                                ? 'Live'
                                                : micrositeData.status === MICROSITE_STATUS.INACTIVE
                                                  ? 'Inactive'
                                                  : 'Draft'}
                                        </div>
                                    </div>

                                    {micrositeData.storeBrandName && (
                                        <div>
                                            <p className="text-xs text-[#64748b] mb-1">Brand</p>
                                            <p className="text-sm font-semibold text-[#1e293b]">
                                                {micrositeData.storeBrandName}
                                            </p>
                                        </div>
                                    )}

                                    {micrositeData.storeName && (
                                        <div>
                                            <p className="text-xs text-[#64748b] mb-1">Store</p>
                                            <p className="text-sm font-semibold text-[#1e293b]">
                                                {micrositeData.storeName}
                                            </p>
                                        </div>
                                    )}

                                    {micrositeData.url && (
                                        <div>
                                            <p className="text-xs text-[#64748b] mb-1">URL</p>
                                            <a
                                                href={micrositeData.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-[#6e64f0] hover:underline flex items-center gap-1"
                                            >
                                                {micrositeData.url}
                                                <svg
                                                    className="w-3 h-3"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                    />
                                                </svg>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg hover:shadow-lg transition-all font-medium"
                                >
                                    View Microsites
                                </button>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 rounded-full bg-[#fee2e2] flex items-center justify-center mb-4">
                                    <svg
                                        className="w-8 h-8 text-[#ef4444]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#1e293b] mb-2">
                                    Failed to Save Microsite
                                </h3>
                                <p className="text-sm text-[#64748b] text-center">
                                    {errorMessage ||
                                        'An error occurred while saving your microsite. Please try again.'}
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2.5 border border-[#e2e8f0] text-[#64748b] rounded-lg hover:bg-[#f8fafc] transition-colors font-medium"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg hover:shadow-lg transition-all font-medium"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
